/**
 * Per-session initialization configuration for Broadpeak SmartLib.
 * This configuration is applied for each playback session and controls
 * CDN routing, analytics, ad behavior, and playback optimizations.
 */
export interface BroadpeakSessionInitializationData {
  /**
   * CDN configuration used by Broadpeak SmartLib to resolve and route streams.
   */
  broadpeakDomainNames: string;

  /**
   * Analytics host configuration used for QoE and playback reporting.
   */
  analyticsAddress?: string;

  /**
   * NanoCDN gateway hostname used for stream resolution and routing.
   */
  nanoCDNHost?: string;

  /**
   * Unique identifier for user or device tracking across sessions.
   */
  uuid?: string;

  /**
   * GDPR compliance preference controlling data collection and processing.
   */
  gdprPreference?: BroadpeakGDPRPreference;

  /**
   * Device type identifier (e.g., web, mobile, tv, stb).
   */
  deviceType?: string;

  /**
   * Interval in milliseconds used to keep the session alive.
   */
  sessionKeepAliveFrequency?: number;

  /**
   * Retry delay in milliseconds for NanoCDN resolving requests.
   */
  nanoCDNResolvingRetryDelay?: number;

  /**
   * Custom analytics parameters sent to Broadpeak analytics backend.
   */
  customParams?: Record<string, any>;

  /**
   * Custom ad parameters forwarded to Broadpeak ad decisioning services.
   */
  adParams?: Record<string, any>;

  /**
   * Logging level used by Broadpeak SmartLib for this session.
   */
  loggerLevel?: BroadpeakLoggerLevel;

  /**
   * User-Agent string forwarded to Broadpeak backend services.
   */
  userAgent?: string;
}

/**
 * GDPR privacy preference for Broadpeak SmartLib.
 */
export enum BroadpeakGDPRPreference {
  GDPR_DELETE = 1,
  GDPR_ANONYMIZED = 2,
  GDPR_ENCRYPTED = 3,
  GDPR_CLEAR = 4,
}

/**
 * Logging levels for Broadpeak SmartLib.
 */
export enum BroadpeakLoggerLevel {
  BASIC = 0,
  VERBOSE = 1,
  OFF = -1,
}
